import { useState, useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setVisualAIActiveItemIndex, setVisualAIPlaylistItems } from '@/store/slices/assetStudioSlice';
import { RootState } from '@/store/store';
import { useAppFilters } from '@/hooks';
import { useExtractVisualAIEvidences } from './useExtractVisualAIEvidences';
import { useFetchVisualAIData } from './useFetchVisualAIData';

export const createComponentEnum = (componentsArray: any[]) => {
  if (!componentsArray) return;
  return Object.freeze(
    Object.fromEntries(
      componentsArray?.map((c) => [
        c.component_name.replace(/\s+/g, ''), // Key: RockSize
        c.component_name.toLowerCase(), // Value: "rock size"
      ])
    )
  );
};

export const useVisualAI = ({ asset, components, diagnostics }: { asset: any; components: any; diagnostics: any }) => {
  const dispatch = useDispatch();
  const { selectedComponent, evidenceResourceId } = useAppFilters();
  const { visualAIPlaylistItems, visualAIActiveItemIndex, selectedDiagnostic } = useSelector(
    (state: RootState) => state.assetsStudio
  );
  const { cameraDiagnostics } = useExtractVisualAIEvidences(diagnostics);

  const [selectedEvidence, setSelectedEvidence] = useState<string | null>(null);

  const currentComponent = useMemo(() => {
    return components?.find((el: any) => el.component_id === selectedComponent);
  }, [selectedComponent, components]);

  const ComponentEnum = useMemo(() => {
    return createComponentEnum(components);
  }, [components]);

  const { isError, isLoading, isFetching, rockSizeData, beltMisalignmentData } = useFetchVisualAIData({
    asset,
  });

  const isFlotationAsset = asset?.asset_name?.toLowerCase().includes('flotation') ?? false;

  // Track if rock size data has been fetched at least once (prevents race condition after cache refresh)
  const isRockSizeDataFetched = useMemo(() => {
    return rockSizeData !== undefined || (!isLoading && !isFetching);
  }, [rockSizeData, isLoading, isFetching]);

  const resetVisualAi = useCallback(() => {
    dispatch(setVisualAIActiveItemIndex(null));
    dispatch(setVisualAIPlaylistItems(null));
  }, [dispatch]);

  // update playlist on cameraDiagnostics change
  // Fixed: Use useEffect instead of useMemo for side effects (dispatch calls)
  useEffect(() => {
    const cameraDiagnosticList = [...cameraDiagnostics].reverse();
    const currentComponentName = currentComponent?.component_name;
    const currentComponentID = currentComponent?.component_id;
    const isBeltComponent = currentComponentName?.toLowerCase().includes(ComponentEnum.Belt);
    const isRockSizeComponent = currentComponentName?.toLowerCase().includes(ComponentEnum.RockSize);
    const isCrusherUnit = currentComponentName?.toLowerCase().includes(ComponentEnum.CrusherUnit);

    if (!cameraDiagnostics?.length) {
      dispatch(setVisualAIPlaylistItems([]));
      dispatch(setVisualAIActiveItemIndex(null));
      return;
    }
    let filteredDiagnostics = [...cameraDiagnosticList];
    filteredDiagnostics = filteredDiagnostics.filter((diag) => diag.component_id === currentComponentID);

    // For crusher units, filter by measurement days only if rockSizeData has loaded
    if (isCrusherUnit) {
      // Only filter if we have rockSizeData - otherwise show all diagnostics (prevents race condition)
      if (rockSizeData?.[0]?.measurements?.x?.length) {
        const measurementDays = new Set(rockSizeData[0].measurements.x.map((ts) => ts.split('T')[0]));
        filteredDiagnostics = filteredDiagnostics.filter((diag) => measurementDays.has(diag.timestamp.split('T')[0]));
      }
      // If rockSizeData is still loading, don't filter yet - wait for next render
    }

    // For belt components, also filter by measurement days
    if (isBeltComponent && beltMisalignmentData?.[0]?.measurements?.x?.length) {
      const measurementDays = new Set(beltMisalignmentData[0].measurements.x.map((ts) => ts.split('T')[0]));

      filteredDiagnostics = filteredDiagnostics.filter((diag) => measurementDays.has(diag.timestamp.split('T')[0]));

    }

    // Only clear diagnostics for Rock Size if data has been fetched AND is empty
    // Don't clear while still loading (prevents "No evidence found" flash after cache refresh)
    if (isRockSizeComponent && isRockSizeDataFetched && !rockSizeData?.length) {
      filteredDiagnostics = [];
    }

    dispatch(setVisualAIPlaylistItems(filteredDiagnostics));

    if (filteredDiagnostics.length > 0) {
      dispatch(setVisualAIActiveItemIndex((selectedDiagnostic?.[0] || evidenceResourceId) ? null : 0));
    }
  }, [
    cameraDiagnostics,
    beltMisalignmentData,
    currentComponent,
    dispatch,
    ComponentEnum,
    rockSizeData,
    isRockSizeDataFetched,
  ]);

  // Match selected diagnostic date (from timeline) to correct playlist item
  // selectedDiagnostic is a Redux tuple [timestamp, data] that updates live when user picks a date
  useEffect(() => {
    const diagnosticTimestamp = selectedDiagnostic?.[0];
    if (!diagnosticTimestamp || !visualAIPlaylistItems?.length) return;
    const selectedDate = diagnosticTimestamp.split('T')[0];

    // First try exact timestamp match
    let playIndex = visualAIPlaylistItems.findIndex((el: any) => {
      return el.uniqueId === el.evidence_resource_id + '_' + diagnosticTimestamp;
    });

    // Fallback: match by date portion only
    if (playIndex === -1) {
      playIndex = visualAIPlaylistItems.findIndex((el: any) => {
        return el.timestamp?.split('T')[0] === selectedDate;
      });
    }

    if (playIndex > -1) dispatch(setVisualAIActiveItemIndex(playIndex));
  }, [visualAIPlaylistItems, selectedDiagnostic, dispatch]);

  // Match evidenceResourceId (from Faults/diagnostics navigation) to correct playlist item
  // Note: don't clear evidenceResourceId here — the playlist rebuilds multiple times as data loads,
  // and we need to keep re-matching until it stabilizes. The param clears naturally on next navigation.
  useEffect(() => {
    if (!evidenceResourceId || !visualAIPlaylistItems?.length) return;
    // Skip if selectedDiagnostic exists — the date-based matching effect above is more precise
    if (selectedDiagnostic?.[0]) return;
    const matchIndex = visualAIPlaylistItems.findIndex((item: any) => item.evidence_resource_id === evidenceResourceId);
    // If match found, select it. If not, keep current selection (auto-select fallback).
    if (matchIndex > -1) dispatch(setVisualAIActiveItemIndex(matchIndex));
  }, [evidenceResourceId, visualAIPlaylistItems, dispatch, selectedDiagnostic]);

  // reset selectedEvidence when currentComponent changes
  // Fixed: Use useEffect instead of useMemo for side effects (setState)
  useEffect(() => {
    setSelectedEvidence(null);
  }, [currentComponent]);

  // @TODO: check if revision is needed
  // Fixed: Removed Math.random() which caused unnecessary re-renders
  const revision = useMemo(() => {
    return selectedEvidence || visualAIActiveItemIndex > -1 || asset ? 1 : 0;
  }, [selectedEvidence, visualAIActiveItemIndex, asset]);

  // update selected evidence when active playlist item changes
  const updatedSelectedEvidence = useMemo(() => {
    return visualAIActiveItemIndex > -1 && visualAIPlaylistItems
      ? visualAIPlaylistItems[visualAIActiveItemIndex]?.uniqueId
      : null;
  }, [visualAIActiveItemIndex, visualAIPlaylistItems]);

  if (selectedEvidence !== updatedSelectedEvidence) {
    setSelectedEvidence(updatedSelectedEvidence);
  }

  return {
    currentComponent,
    isError,
    isLoading,
    isFetching,
    rockSizeData,
    beltMisalignmentData,
    selectedEvidence,
    setSelectedEvidence,
    revision,
    visualAIPlaylistItems,
    visualAIActiveItemIndex,
    cameraDiagnostics,
    selectedComponent,
    resetVisualAi,
    isFlotationAsset,
  };
};
